import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield } from 'lucide-react';

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-white pb-24 md:pb-12" data-testid="privacy-policy-page">
      <div className="max-w-3xl mx-auto px-4 py-8 md:py-12">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900 mb-6">
          <ArrowLeft className="w-4 h-4" />वापस जाएं
        </Link>
        <div className="flex items-center gap-3 mb-8">
          <Shield className="w-8 h-8 text-red-600" />
          <h1 className="font-outfit font-bold text-3xl text-gray-900">गोपनीयता नीति / Privacy Policy</h1>
        </div>
        <p className="text-sm text-gray-500 mb-8">अंतिम अपडेट / Last Updated: 15 April 2026</p>

        <div className="prose prose-gray max-w-none space-y-6 text-gray-700 leading-relaxed">
          <section>
            <h2 className="font-outfit font-bold text-xl text-gray-900 mb-3">1. परिचय / Introduction</h2>
            <p>गुमसुदा ("हम", "हमारा", "एप्लिकेशन") एक लापता व्यक्ति खोज पोर्टल है जो लापता और अज्ञात व्यक्तियों की जानकारी साझा करके परिवारों को पुनर्मिलन में सहायता करता है। यह गोपनीयता नीति बताती है कि हम आपकी व्यक्तिगत जानकारी को कैसे एकत्र, उपयोग, संग्रहित और संरक्षित करते हैं।</p>
            <p>Gumsuda ("we", "our", "application") is a missing person search portal that assists families in reunification by sharing information about missing and unidentified persons. This Privacy Policy describes how we collect, use, store, and protect your personal information.</p>
          </section>

          <section>
            <h2 className="font-outfit font-bold text-xl text-gray-900 mb-3">2. हम कौन सी जानकारी एकत्र करते हैं / Information We Collect</h2>
            <h3 className="font-semibold text-lg mt-4 mb-2">a) स्वेच्छा से दी गई जानकारी / Information You Voluntarily Provide:</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li>लापता/अज्ञात व्यक्ति का नाम, उम्र, लिंग, शारीरिक विवरण / Name, age, gender, physical description of missing/unknown person</li>
              <li>फोटोग्राफ / Photographs</li>
              <li>अंतिम ज्ञात स्थान / Last known location</li>
              <li>रिपोर्टर का नाम और संपर्क नंबर / Reporter's name and contact number</li>
              <li>पुलिस स्टेशन की जानकारी / Police station information</li>
              <li>सुझाव/टिप्स (नाम और मोबाइल नंबर) / Tips (name and mobile number)</li>
            </ul>
            <h3 className="font-semibold text-lg mt-4 mb-2">b) स्वचालित रूप से एकत्र जानकारी / Automatically Collected Information:</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li>डिवाइस की जानकारी (ब्राउज़र प्रकार, ऑपरेटिंग सिस्टम) / Device information (browser type, operating system)</li>
              <li>IP पता / IP address</li>
              <li>ऐप उपयोग डेटा / App usage data</li>
            </ul>
          </section>

          <section>
            <h2 className="font-outfit font-bold text-xl text-gray-900 mb-3">3. जानकारी का उपयोग / How We Use Information</h2>
            <p>हम एकत्रित जानकारी का उपयोग निम्नलिखित उद्देश्यों के लिए करते हैं:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>लापता व्यक्तियों की रिपोर्ट प्रदर्शित करना / Displaying missing person reports</li>
              <li>अज्ञात व्यक्तियों की पहचान में सहायता / Assisting in identification of unknown persons</li>
              <li>AI आधारित फेस मैचिंग सेवा प्रदान करना / Providing AI-based face matching service</li>
              <li>रिपोर्ट करने वाले व्यक्ति से संपर्क / Contacting the reporting person</li>
              <li>सेवा में सुधार / Improving our service</li>
              <li>दुरुपयोग को रोकना / Preventing misuse</li>
            </ul>
          </section>

          <section>
            <h2 className="font-outfit font-bold text-xl text-gray-900 mb-3">4. AI फेस मैचिंग / AI Face Matching</h2>
            <p>हमारा एप्लिकेशन AI-आधारित फेस मैचिंग तकनीक का उपयोग करता है जो अपलोड की गई तस्वीरों की तुलना डेटाबेस में मौजूद तस्वीरों से करती है। यह सेवा केवल लापता व्यक्तियों की पहचान के उद्देश्य से है।</p>
            <p>Our application uses AI-based face matching technology that compares uploaded photographs with those in our database. This service is solely for the purpose of identifying missing persons.</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>अपलोड की गई तस्वीरें केवल मैचिंग के लिए उपयोग होती हैं / Uploaded photos are used only for matching purposes</li>
              <li>फेस डेटा तृतीय पक्षों को नहीं बेचा जाता / Face data is not sold to third parties</li>
              <li>प्रोसेसिंग सुरक्षित सर्वर पर होती है / Processing occurs on secure servers</li>
            </ul>
          </section>

          <section>
            <h2 className="font-outfit font-bold text-xl text-gray-900 mb-3">5. डेटा संग्रहण और सुरक्षा / Data Storage & Security</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>सभी डेटा सुरक्षित, एन्क्रिप्टेड सर्वर पर संग्रहित होता है / All data is stored on secure, encrypted servers</li>
              <li>HTTPS एन्क्रिप्शन द्वारा डेटा ट्रांसमिशन सुरक्षित / Data transmission secured via HTTPS encryption</li>
              <li>एडमिन एक्सेस पासवर्ड-संरक्षित और JWT टोकन द्वारा प्रमाणित / Admin access is password-protected and JWT token authenticated</li>
              <li>नियमित सुरक्षा ऑडिट / Regular security audits</li>
            </ul>
          </section>

          <section>
            <h2 className="font-outfit font-bold text-xl text-gray-900 mb-3">6. डेटा प्रतिधारण / Data Retention</h2>
            <p>लापता व्यक्ति की रिपोर्ट तब तक सक्रिय रहती है जब तक व्यक्ति नहीं मिल जाता या रिपोर्टर द्वारा हटाने का अनुरोध नहीं किया जाता। "मिल गया" चिह्नित केस 90 दिनों तक संदर्भ के लिए उपलब्ध रहते हैं।</p>
            <p>Missing person reports remain active until the person is found or the reporter requests removal. Cases marked "Found" remain available for reference for 90 days.</p>
          </section>

          <section>
            <h2 className="font-outfit font-bold text-xl text-gray-900 mb-3">7. डेटा साझाकरण / Data Sharing</h2>
            <p>हम आपकी जानकारी निम्नलिखित परिस्थितियों में साझा कर सकते हैं:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>कानून प्रवर्तन एजेंसियों के साथ (कानूनी अनुरोध पर) / With law enforcement agencies (on legal request)</li>
              <li>सार्वजनिक रूप से स्वीकृत केस विवरण (खोज उद्देश्य के लिए) / Publicly approved case details (for search purposes)</li>
              <li>सोशल मीडिया शेयरिंग (उपयोगकर्ता की सहमति से) / Social media sharing (with user consent)</li>
            </ul>
            <p className="font-semibold mt-2">हम व्यक्तिगत डेटा विज्ञापन या मार्केटिंग उद्देश्यों के लिए कभी नहीं बेचते। / We never sell personal data for advertising or marketing purposes.</p>
          </section>

          <section>
            <h2 className="font-outfit font-bold text-xl text-gray-900 mb-3">8. बच्चों की गोपनीयता / Children's Privacy</h2>
            <p>हमारी सेवा लापता बच्चों को खोजने में मदद करती है। बच्चों से संबंधित रिपोर्ट केवल अभिभावक, परिवार के सदस्य, या अधिकृत व्यक्ति द्वारा ही दर्ज की जा सकती है। हम 13 वर्ष से कम उम्र के बच्चों से सीधे कोई जानकारी एकत्र नहीं करते।</p>
            <p>Our service helps in finding missing children. Reports related to children can only be filed by a parent, family member, or authorized person. We do not directly collect any information from children under 13 years of age.</p>
          </section>

          <section>
            <h2 className="font-outfit font-bold text-xl text-gray-900 mb-3">9. आपके अधिकार / Your Rights</h2>
            <p>आपको निम्नलिखित अधिकार प्राप्त हैं:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>अपनी जानकारी तक पहुँच का अधिकार / Right to access your information</li>
              <li>गलत जानकारी को सही कराने का अधिकार / Right to correct inaccurate information</li>
              <li>अपनी रिपोर्ट हटाने का अनुरोध करने का अधिकार / Right to request deletion of your report</li>
              <li>डेटा प्रोसेसिंग पर आपत्ति करने का अधिकार / Right to object to data processing</li>
            </ul>
            <p className="mt-2">इन अधिकारों का उपयोग करने के लिए, कृपया हमसे संपर्क करें। / To exercise these rights, please contact us.</p>
          </section>

          <section>
            <h2 className="font-outfit font-bold text-xl text-gray-900 mb-3">10. तृतीय पक्ष सेवाएं / Third-Party Services</h2>
            <p>हम निम्नलिखित तृतीय पक्ष सेवाओं का उपयोग करते हैं:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>क्लाउड स्टोरेज (फोटो संग्रहण के लिए) / Cloud Storage (for photo storage)</li>
              <li>AI सेवाएं (फेस मैचिंग के लिए) / AI Services (for face matching)</li>
            </ul>
            <p>इन सेवाओं की अपनी गोपनीयता नीतियां हैं और हम उनके डेटा प्रथाओं के लिए जिम्मेदार नहीं हैं।</p>
          </section>

          <section>
            <h2 className="font-outfit font-bold text-xl text-gray-900 mb-3">11. अनुमतियां / Permissions</h2>
            <p>हमारा ऐप निम्नलिखित अनुमतियों का उपयोग करता है:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>कैमरा / Camera:</strong> फोटो अपलोड और फेस मैचिंग के लिए / For photo upload and face matching</li>
              <li><strong>स्टोरेज / Storage:</strong> पोस्टर डाउनलोड करने के लिए / For downloading posters</li>
              <li><strong>इंटरनेट / Internet:</strong> सेवा के संचालन के लिए / For service operation</li>
            </ul>
          </section>

          <section>
            <h2 className="font-outfit font-bold text-xl text-gray-900 mb-3">12. नीति में परिवर्तन / Changes to Policy</h2>
            <p>हम समय-समय पर इस गोपनीयता नीति को अपडेट कर सकते हैं। किसी भी महत्वपूर्ण परिवर्तन की सूचना ऐप के माध्यम से दी जाएगी।</p>
            <p>We may update this Privacy Policy from time to time. Any significant changes will be notified through the app.</p>
          </section>

          <section>
            <h2 className="font-outfit font-bold text-xl text-gray-900 mb-3">13. संपर्क / Contact Us</h2>
            <p>गोपनीयता संबंधी प्रश्नों के लिए संपर्क करें:</p>
            <ul className="list-none space-y-1">
              <li><strong>ईमेल / Email:</strong> privacy@gumsuda.com</li>
              <li><strong>वेबसाइट / Website:</strong> https://gumsuda.com</li>
            </ul>
          </section>
        </div>
      </div>
    </div>
  );
}
